import React, { useState } from 'react';
import { useNavigate, Navigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Landmark, Phone, Lock, Eye, EyeOff, ArrowRight } from 'lucide-react';
import { useAuth } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';

const LoginPage = () => {
  const navigate = useNavigate();
  const { login, isAuthenticated, isAdmin, loading } = useAuth();
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  // Redirect if already authenticated
  if (!loading && isAuthenticated) {
    return <Navigate to={isAdmin ? '/admin/dashboard' : '/member/dashboard'} replace />;
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    
    const result = await login(phone, password);
    
    if (result.success) {
      if (result.user.role === 'admin') {
        navigate('/admin/dashboard');
      } else {
        navigate('/member/dashboard');
      }
    }
    
    setIsLoading(false);
  };

  return (
    <div className="min-h-screen flex">
      {/* Left Side - Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8 lg:p-16 bg-white">
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.5 }}
          className="w-full max-w-md"
        >
          {/* Logo */}
          <div className="flex items-center gap-3 mb-12">
            <div className="w-12 h-12 rounded-xl bg-[#0A2540] flex items-center justify-center">
              <Landmark className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="font-semibold text-[#0A2540] text-lg" style={{ fontFamily: 'Poppins, sans-serif' }}>
                Shri Bhavani
              </h1>
              <p className="text-xs text-[#425466]">Self Help Group</p>
            </div>
          </div>

          {/* Welcome Text */}
          <div className="mb-8">
            <h2 className="text-3xl font-bold text-[#0A2540] mb-2" style={{ fontFamily: 'Poppins, sans-serif' }}>
              Welcome Back
            </h2>
            <p className="text-[#425466]">
              Enter your credentials to access your account
            </p>
          </div>

          {/* Login Form */}
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="phone" className="text-[#0A2540] font-medium">
                Phone Number
              </Label>
              <div className="relative">
                <Phone className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#425466]" />
                <Input
                  id="phone"
                  type="tel"
                  placeholder="Enter your phone number"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  data-testid="login-phone-input"
                  className="pl-12 h-12 border-[#E2E8F0] focus:border-[#2962FF] focus:ring-[#2962FF]/20"
                  required
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-[#0A2540] font-medium">
                Password
              </Label>
              <div className="relative">
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#425466]" />
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Enter your password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  data-testid="login-password-input"
                  className="pl-12 pr-12 h-12 border-[#E2E8F0] focus:border-[#2962FF] focus:ring-[#2962FF]/20"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-[#425466] hover:text-[#0A2540]"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            <Button
              type="submit"
              data-testid="login-submit-btn"
              disabled={isLoading}
              className="w-full h-12 bg-[#0A2540] hover:bg-[#163A5F] text-white font-medium rounded-xl shadow-lg hover:shadow-xl transition-all"
            >
              {isLoading ? (
                <div className="flex items-center gap-2">
                  <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  Signing in...
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  Sign In <ArrowRight className="w-5 h-5" />
                </div>
              )}
            </Button>
          </form>

          {/* Back Link */}
          <div className="mt-8 text-center">
            <a 
              href="/" 
              className="text-[#425466] hover:text-[#0A2540] text-sm transition-colors"
            >
              ← Back to Homepage
            </a>
          </div>
        </motion.div>
      </div>

      {/* Right Side - Image */}
      <div className="hidden lg:block lg:w-1/2 relative bg-[#0A2540]">
        <div className="absolute inset-0">
          <img
            src="https://images.pexels.com/photos/7108590/pexels-photo-7108590.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
            alt="Financial growth"
            className="w-full h-full object-cover opacity-40"
          />
          <div className="absolute inset-0 bg-gradient-to-br from-[#0A2540]/80 to-[#0A2540]/60" />
        </div>
        
        <div className="absolute inset-0 flex items-center justify-center p-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="text-center"
          >
            <div className="w-20 h-20 rounded-2xl bg-white/10 backdrop-blur flex items-center justify-center mx-auto mb-8">
              <Landmark className="w-10 h-10 text-white" />
            </div>
            <h2 className="text-4xl font-bold text-white mb-4" style={{ fontFamily: 'Poppins, sans-serif' }}>
              Building Wealth
              <br />
              <span className="text-[#00C853]">Together</span>
            </h2>
            <p className="text-gray-300 max-w-md mx-auto">
              Join our community of financially empowered members. Track contributions, manage loans, and grow together.
            </p>
            
            {/* Stats */}
            <div className="mt-12 grid grid-cols-3 gap-6">
              {[
                { label: 'Members', value: '20+' },
                { label: 'Fund Pool', value: '₹10L+' },
                { label: 'Interest', value: '1%' }
              ].map((stat, index) => (
                <div key={index} className="text-center">
                  <p className="text-3xl font-bold text-white" style={{ fontFamily: 'Poppins, sans-serif' }}>
                    {stat.value}
                  </p>
                  <p className="text-sm text-gray-400">{stat.label}</p>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </div>
      
      {/* Footer */}
      <div className="absolute bottom-0 left-0 right-0 bg-white/5 backdrop-blur py-3 text-center">
        <p className="text-sm text-gray-400">
          Designed By: <span className="text-white font-semibold">Viral Boostup</span> | 
          <a href="tel:+919110278059" className="text-[#00C853] hover:underline ml-1">+91 9110278059</a>
        </p>
      </div>
    </div>
  );
};

export default LoginPage;
